import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ShoppingBag, Trash2, X } from "lucide-react";
import { buttonClass } from "@/components/ui/buttonStyles";
import { STORE_API_URL } from "@/config/constants";
import { useCart } from "@/hooks/useCart";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useLocale } from "@/i18n/useLocale";
import { trackEvent } from "@/utils/analytics";
import { formatPrice } from "@/utils/price";

/** Side sheet with the cart; "Pay" hands off to Stripe Checkout. */
export function CartDrawer() {
  const cart = useCart();
  const { t } = useAppTranslation("resourcesPage");
  const locale = useLocale();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [state, setState] = useState<"idle" | "redirecting" | "error">("idle");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (cart.isOpen && !dialog.open) {
      setState("idle");
      dialog.showModal();
    } else if (!cart.isOpen && dialog.open) {
      dialog.close();
    }
  }, [cart.isOpen]);

  async function checkout() {
    setState("redirecting");
    trackEvent("checkout_start", { items: cart.count, total: cart.totalCents });
    try {
      const res = await fetch(`${STORE_API_URL}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: cart.lines.map((line) => line.id), locale }),
      });
      const body = (await res.json().catch(() => ({}))) as { url?: string };
      if (!res.ok || !body.url) throw new Error("checkout failed");
      window.location.assign(body.url);
    } catch {
      setState("error");
    }
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      onClose={cart.close}
      onClick={(e) => {
        if (e.target === dialogRef.current) cart.close();
      }}
      className="m-0 ml-auto h-dvh max-h-dvh w-full max-w-md bg-white p-0 text-ink shadow-lit-lg backdrop:bg-brand-primary/55 backdrop:backdrop-blur-sm sm:rounded-l-[1.75rem]"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-brand-primary/8 px-6 py-5">
          <h2 id={titleId} className="font-display text-2xl font-semibold text-brand-primary">
            {t("cartTitle")}
          </h2>
          <button
            type="button"
            onClick={cart.close}
            aria-label={t("cartClose")}
            className="grid size-10 place-items-center rounded-full text-brand-primary transition-colors hover:bg-teal-soft"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        {cart.count === 0 ? (
          <div className="light-pool flex flex-1 flex-col items-center justify-center gap-5 px-6 text-center [--pool-x:50%] [--pool-y:40%]">
            <span className="grid size-16 place-items-center rounded-full bg-teal-soft text-teal-strong">
              <ShoppingBag className="size-7" strokeWidth={1.5} aria-hidden="true" />
            </span>
            <p className="text-ink-soft">{t("cartEmpty")}</p>
            <Link to="/recursos#catalogo" onClick={cart.close} className={buttonClass("outline", "md")}>
              {t("cartBrowse")}
            </Link>
          </div>
        ) : (
          <>
            <ul className="flex-1 divide-y divide-brand-primary/8 overflow-y-auto px-6">
              {cart.lines.map((line) => (
                <li key={line.id} className="flex items-center gap-4 py-5">
                  <div className="size-18 shrink-0 overflow-hidden rounded-xl bg-teal-soft">
                    {line.coverSrc ? (
                      <img
                        src={line.coverSrc}
                        alt=""
                        width={72}
                        height={72}
                        className="size-full object-cover"
                        /* Covers are signed URLs that expire; a stale cart just shows the plate. */
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-teal-strong">{line.type}</p>
                    <p className="mt-0.5 font-display text-lg font-semibold leading-snug text-brand-primary">
                      {line.title}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-ink-soft">
                      {formatPrice(line.priceCents, line.currency, locale)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => cart.remove(line.id)}
                    aria-label={t("cartRemove", { title: line.title })}
                    className="grid size-10 shrink-0 place-items-center rounded-full text-ink-muted transition-colors hover:bg-teal-soft hover:text-brand-primary"
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>

            <div className="border-t border-brand-primary/8 bg-brand-bg px-6 pb-6 pt-5">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold text-ink-soft">{t("cartTotal")}</span>
                <span className="font-display text-3xl font-semibold text-brand-primary">
                  {formatPrice(cart.totalCents, cart.currency, locale)}
                </span>
              </div>
              {state === "error" ? (
                <p role="alert" className="mt-3 text-sm text-red-700">
                  {t("cartError")}
                </p>
              ) : null}
              <button
                type="button"
                onClick={checkout}
                disabled={state === "redirecting"}
                className={buttonClass("primary", "lg", "mt-5 w-full")}
              >
                <Lock className="size-4" aria-hidden="true" />
                {state === "redirecting" ? t("cartRedirecting") : t("cartCheckout")}
              </button>
              <p className="mt-3 text-center text-xs leading-relaxed text-ink-muted">{t("cartNote")}</p>
            </div>
          </>
        )}
      </div>
    </dialog>
  );
}
